import FormInput from '@form/FormInput';
import FormWatcher from '@form/FormWatcher';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAuth from '@hooks/useAuth';
import { Key, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useClubSlug } from '../../../Clubs/hooks';
import { Space } from '../../types';
import MemberSelection from '../MemberSelection';
import BookerInput from './BookerInput';

// id KioskBoard gives its on-screen keyboard element
const KIOSK_KEYBOARD_ID = 'KioskBoard-VirtualKeyboard';

interface Props {
    name?: string;
    hideSelf?: boolean;
    spaceId?: Key;
    clubSlug?: string;
    /**
     * Overrides the space's allow_empty_booker_email. For a selection that spans several
     * spaces there is no single space to ask, and the server decides per allocation anyway.
     */
    allowEmptyEmail?: boolean;
}

const BookersFieldArray = ({
    name = 'bookers',
    hideSelf,
    spaceId: _spaceId,
    clubSlug: _clubSlug,
    allowEmptyEmail
}: Props) => {
    const { t } = useTranslation('space-manager');
    const { control, watch, setValue } = useFormContext();
    const { user } = useAuth();
    const { fields, append, remove } = useFieldArray({ control, name });
    const spaceId = watch('space_id') ?? _spaceId;
    // load the active space so we can honour its per-location booker settings
    const { data: spaceResponse } = useSWR<{ data: Space }>(spaceId ? `space-manager/space/${spaceId}` : null);
    // when enabled, an additional booker without an email is saved as a guest
    const allowEmptyBookerEmail = allowEmptyEmail ?? spaceResponse?.data?.settings?.allow_empty_booker_email ?? false;
    // `disallow_guest_bookers` is deliberately not read here: this button is the only
    // way to add a co-player at all on the web, so hiding it would remove the member
    // path along with the guest one. The server rejects an unassigned co-player.
    const [mainBookerName, mainBookerEmail] = watch(['user_name', 'user_email']);
    const clubSlug = useClubSlug(_clubSlug);

    const [mainBooker, setMainBooker] = useState<{
        user_name: string;
        user_email: string;
        member_number?: string;
        member_id?: Key;
    }>({
        user_name: mainBookerName ?? user?.name,
        user_email: mainBookerEmail ?? user?.email
    });

    // push the local main-booker state (name/email + selected member) back into the form
    useEffect(() => {
        setValue('user_name', mainBooker.user_name);
        setValue('user_email', mainBooker.user_email);
        setValue('member_number', mainBooker.member_number);
        setValue('member_id', mainBooker.member_id);
    }, [mainBooker]);

    // mirror the form's name/email into local state for the main-booker card —
    // keep the same reference when nothing changed so this doesn't ping-pong with
    // the setValue effect above and spin the render loop
    useEffect(() => {
        const nextName = mainBookerName ?? user?.name;
        const nextEmail = mainBookerEmail ?? user?.email;
        setMainBooker((prev) =>
            prev.user_name === nextName && prev.user_email === nextEmail
                ? prev
                : { ...prev, user_name: nextName, user_email: nextEmail }
        );
    }, [mainBookerName, mainBookerEmail, user?.name, user?.email]);

    // the kiosk keyboard opens on a `focus` event, but a scripted focus() does not
    // reliably emit one — so emit it ourselves. It also only builds a keyboard while
    // none is in the DOM, hence we wait out the teardown of the previous one first,
    // otherwise the visible keyboard would still type into the old input.
    const focusBooker = (field: string, attempt = 0) => {
        if (document.getElementById(KIOSK_KEYBOARD_ID) && attempt < 20) {
            setTimeout(() => focusBooker(field, attempt + 1), 25);
            return;
        }

        const input = document.querySelector<HTMLInputElement>(`input[name="${field}"]`);
        if (!input) return;

        input.focus();
        input.dispatchEvent(new FocusEvent('focus'));
    };

    // focus the fresh name field so the kiosk keyboard opens without an extra tap
    const handleAppend = () => {
        const index = fields.length;
        append({});
        // one tick, so the input exists and the virtual keyboard has picked it up
        setTimeout(() => focusBooker(`${name}.${index}.user_name`), 0);
    };

    return (
        <div className="tw-my-4 tw-flex tw-flex-col tw-gap-4">
            {!hideSelf && (
                <div className="tw-card-bordered tw-flex tw-flex-col tw-p-2 tw-px-4 tw-pt-1">
                    <div className="tw-flex tw-min-h-[40px] tw-items-center tw-justify-between">
                        <span className="tw-text-accents-8">{t('bookers.mainBooker')}</span>
                        {mainBooker.member_id && (
                            <span className="tw-text-xs tw-italic tw-text-accents-6">
                                {t('bookers.memberNumber', { number: mainBooker.member_number })}
                            </span>
                        )}
                        <MemberSelection
                            clubSlug={clubSlug}
                            onSelect={(member) =>
                                setMainBooker({
                                    user_email: mainBooker.user_email,
                                    user_name: member.name,
                                    member_number: member.number,
                                    member_id: member.id
                                })
                            }
                        />
                    </div>
                    <span className="tw-text-lg tw-font-bold tw-text-accents-7">{mainBooker.user_name}</span>
                    <span className="tw-text-md tw-italic tw-text-accents-6">{mainBooker.user_email}</span>
                </div>
            )}
            {fields.map((field, index) => (
                <div className="tw-card-bordered tw-p-2 tw-pt-1" key={field.id}>
                    <div className="tw-flex tw-min-h-[40px] tw-items-center tw-justify-between tw-px-2">
                        <span className="tw tw-text-accents-8">{t('bookers.playerNumber', { number: index + 1 })}</span>
                        <FormWatcher name={`${name}.${index}.member_number`}>
                            {(memberId) =>
                                // arrives pre-masked from the API (CompactMemberResource), so it is
                                // rendered as-is — no client-side blurring needed
                                !!memberId && (
                                    <span className="tw-text-xs tw-italic tw-text-accents-6">
                                        {t('bookers.memberNumber', { number: memberId })}
                                    </span>
                                )
                            }
                        </FormWatcher>
                        <button
                            type="button"
                            onClick={() => remove(index)}
                            className="tw-border-none tw-bg-transparent tw-p-2 tw-text-accents-5"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                    <BookerInput
                        prefix={`${name}.${index}`}
                        spaceId={spaceId}
                        clubSlug={clubSlug}
                    />
                    <FormWatcher name={[`${name}.${index}.email_hidden`, `${name}.${index}.email_masked`]}>
                        {/* a masked address is shown read-only from its own field so the
                            real one is never submitted — the server fills it in. When the
                            API withheld the address entirely there is nothing to show, so
                            the field is dropped rather than rendered empty and disabled */}
                        {([emailHidden, emailMasked]) => {
                            if (emailHidden)
                                return emailMasked ? (
                                    <FormInput
                                        type="text"
                                        name={`${name}.${index}.email_masked`}
                                        placeholder={t('bookers.emailPlaceholder')}
                                        disabled
                                    />
                                ) : undefined;

                            return (
                                <FormInput
                                    type="email"
                                    name={`${name}.${index}.user_email`}
                                    placeholder={t('bookers.emailPlaceholder')}
                                />
                            );
                        }}
                    </FormWatcher>
                    {allowEmptyBookerEmail && (
                        <FormWatcher name={[`${name}.${index}.user_email`, `${name}.${index}.email_hidden`]}>
                            {/* a withheld email still belongs to a member, so it is no guest */}
                            {([email, emailHidden]) =>
                                !email &&
                                !emailHidden && (
                                    <span className="tw-mt-1 tw-block tw-text-center tw-text-xs tw-italic tw-text-accents-6">
                                        {t('bookers.guestNotice')}
                                    </span>
                                )
                            }
                        </FormWatcher>
                    )}
                </div>
            ))}
            <div
                onClick={handleAppend}
                className="tw-card-flat tw-card-clickable tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-accents-7"
            >
                <FontAwesomeIcon icon={faPlus} />
                <span>{t('bookers.addGuest')}</span>
            </div>
        </div>
    );
};

export default BookersFieldArray;
